// Índice principal de todos los documentos organizados por categorías
import comienzo from './comienzo/index.js';
import general from './general/index.js';
import facebook from './facebook/index.js';
import instagram from './instagram/index.js';
import whatsapp from './whatsapp/index.js';
import flujos from './flujos/index.js';
import webchat from './webchat/index.js';
import tiktok from './tiktok/index.js';
import etiqueta_blanca from './etiqueta-blanca/index.js';

// Exportar todas las categorías definidas
export const CATEGORIES = {
  COMIENZO: 'Comienzo',
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook', 
  WHATSAPP: 'WhatsApp',
  TIKTOK: 'TikTok',
  FLUJOS: 'Flujos',
  WEBCHAT: 'Webchat',
  NAVEGACION: 'Navegación',
  INTELIGENCIA_ARTIFICIAL: 'Inteligencia Artificial',
  INTEGRACIONES: 'Integraciones',
  GENERAL: 'General',
  ETIQUETA_BLANCA: 'Etiqueta Blanca'
};

// Exportar todos los documentos
export const documentsData = [
  ...comienzo,
  ...general,
  ...facebook,
  ...instagram,
  ...whatsapp,
  ...tiktok,
  ...flujos,
  ...webchat,
  ...etiqueta_blanca,
];

// Exportar documentos por categoría
export const documentsByCategory = {
  'Comienzo': comienzo,
  'General': general,
  'Facebook': facebook,
  'Instagram': instagram,
  'WhatsApp': whatsapp,
  'TikTok': tiktok,
  'Flujos': flujos,
  'Webchat': webchat,
  'Etiqueta Blanca': etiqueta_blanca,
};

// ============== FUNCIONES UTILITARIAS ==============

// Obtener documento por ID
export const getDocumentById = (id) => {
  return documentsData.find(doc => doc.id === id);
};

// Obtener todas las categorías disponibles (incluyendo "Todas" para filtrado)
export const getCategories = () => {
  return ['Todas', ...Object.values(CATEGORIES)];
};

// Filtrar documentos por término de búsqueda y categoría
export const filterDocuments = (searchTerm = '', category = 'Todas') => {
  let filtered = documentsData;

  // Filtrar por categoría
  if (category !== 'Todas') {
    filtered = filtered.filter(doc => doc.category === category);
  }

  // Filtrar por término de búsqueda
  if (searchTerm.trim()) {
    const term = searchTerm.toLowerCase();
    filtered = filtered.filter(doc => 
      doc.title.toLowerCase().includes(term) ||
      doc.description.toLowerCase().includes(term) ||
      doc.steps?.some(step => 
        step.title.toLowerCase().includes(term) ||
        step.content.toLowerCase().includes(term)
      ) ||
      doc.explanations?.some(explanation => 
        explanation.title.toLowerCase().includes(term) ||
        explanation.content.toLowerCase().includes(term)
      ) ||
      doc.tags?.some(tag => tag.toLowerCase().includes(term))
    );
  }

  return filtered;
};

// Obtener documentos por categoría específica
export const getDocumentsByCategory = (category) => {
  if (category === 'Todas') return documentsData;
  return documentsData.filter(doc => doc.category === category);
};

// Obtener documentos relacionados (misma categoría, excluyendo el actual)
export const getRelatedDocuments = (currentDocId, limit = 3) => {
  const currentDoc = getDocumentById(currentDocId);
  if (!currentDoc) return [];
  
  return documentsData
    .filter(doc => doc.id !== currentDocId && doc.category === currentDoc.category)
    .slice(0, limit);
};

// Función helper para validar la estructura de un documento
export const validateDocumentStructure = (document) => {
  const requiredFields = ['id', 'title', 'category', 'description'];
  const missingFields = requiredFields.filter(field => !document[field]);
  
  if (missingFields.length > 0) {
    throw new Error(`Faltan campos requeridos: ${missingFields.join(', ')}`);
  }
  
  if (!Object.values(CATEGORIES).includes(document.category)) {
    throw new Error(`Categoría inválida: ${document.category}. Debe ser una de: ${Object.values(CATEGORIES).join(', ')}`);
  }
  
  // Validar que tenga al menos explanations o steps
  const hasExplanations = document.explanations && Array.isArray(document.explanations) && document.explanations.length > 0;
  const hasSteps = document.steps && Array.isArray(document.steps) && document.steps.length > 0;
  
  if (!hasExplanations && !hasSteps) {
    throw new Error('El documento debe tener al menos una explicación o un paso');
  }
  
  // Validar estructura de cada explicación
  if (document.explanations) {
    document.explanations.forEach((explanation, index) => {
      if (!explanation.title || !explanation.content) {
        throw new Error(`La explicación ${index + 1} debe tener título y contenido`);
      }
    });
  }
  
  // Validar estructura de cada paso
  if (document.steps) {
    document.steps.forEach((step, index) => {
      if (!step.title || !step.content) {
        throw new Error(`El paso ${index + 1} debe tener título y contenido`);
      }
    });
  }
  
  return true;
};

// Función para agregar un nuevo documento (con validación)
export const addDocument = (document) => {
  try {
    validateDocumentStructure(document);
    
    // Verificar que el ID sea único
    if (getDocumentById(document.id)) {
      throw new Error(`Ya existe un documento con el ID: ${document.id}`);
    }
    
    documentsData.push(document);
    return { success: true, message: 'Documento agregado exitosamente' };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

// Estadísticas de documentación
export const getDocStats = () => {
  const stats = {
    total: documentsData.length,
    byCategory: {},
    totalExplanations: 0,
    totalSteps: 0,
    totalImages: 0
  };
  
  Object.values(CATEGORIES).forEach(category => {
    stats.byCategory[category] = 0;
  });
  
  documentsData.forEach(doc => {
    stats.byCategory[doc.category] = (stats.byCategory[doc.category] || 0) + 1;
    stats.totalExplanations += doc.explanations?.length || 0;
    stats.totalSteps += (doc.steps?.length || 0) + (doc.explanations?.length || 0); // Incluir explanations en el conteo
    
    // Contar imágenes en explicaciones
    doc.explanations?.forEach(explanation => {
      if (explanation.images) {
        stats.totalImages += explanation.images.length;
      }
    });
    
    // Contar imágenes en pasos
    doc.steps?.forEach(step => {
      if (step.images) {
        stats.totalImages += step.images.length;
      }
    });
    
    // Contar imagen principal
    if (doc.mainImage) {
      stats.totalImages += 1;
    }
  });
  
  return stats;
};

// Funciones para contenido destacado en la página de documentación

// Obtener documentos esenciales/recomendados para empezar
export const getEssentialDocuments = () => {
  // Documentos marcados como esenciales o de la categoría "Comienzo"
  return documentsData
    .filter(doc => 
      doc.category === 'Comienzo' || 
      doc.tags?.includes('esencial') || 
      doc.tags?.includes('principiantes')
    )
    .slice(0, 3);
};

// Obtener documentos más recientes
export const getRecentDocuments = (limit = 3) => {
  return documentsData
    .sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated))
    .slice(0, limit);
};

// Obtener iconos por categoría
export const getCategoryIcon = (category) => {
  const icons = {
    'Comienzo': '',
    'Instagram': '',
    'Facebook': '',
    'WhatsApp': '',
    'TikTok': '🎵',
    'Flujos': '',
    'Webchat': '',
    'Navegación': '',
    'Inteligencia Artificial': '',
    'Integraciones': '',
    'General': '',
    'Etiqueta Blanca': '🏷️'
  };
  return icons[category] || '';
};

// Obtener categorías populares (con más documentos)
export const getPopularCategories = (limit = 6) => {
  const stats = getDocStats();
  return Object.entries(stats.byCategory)
    .filter(([category, count]) => count > 0) // Solo categorías con documentos
    .sort(([,a], [,b]) => b - a) // Ordenar por cantidad de documentos
    .slice(0, limit)
    .map(([category, count]) => ({
      name: category,
      count,
      icon: getCategoryIcon(category),
      description: getCategoryDescription(category)
    }));
};

// Obtener descripción de categoría
export const getCategoryDescription = (category) => {
  const descriptions = {
    'Comienzo': 'Primeros pasos y configuración inicial',
    'Instagram': 'Configuración y automatización de Instagram',
    'Facebook': 'Integración con Facebook y Messenger',
    'WhatsApp': 'Configuración y uso de WhatsApp Business',
    'TikTok': 'Configuración y automatización de TikTok',
    'Flujos': 'Creación de conversaciones automatizadas',
    'Webchat': 'Chat en tu sitio web',
    'Navegación': 'Cómo usar la plataforma',
    'Inteligencia Artificial': 'Configuración y optimización de IA',
    'Integraciones': 'Conecta con tus herramientas favoritas',
    'General': 'Configuraciones y ajustes generales',
    'Etiqueta Blanca': 'Configuración avanzada para socios y revendedores'
  };
  return descriptions[category] || 'Documentación sobre esta categoría';
};
